import { supabase } from '@/lib/supabase'
import type { Database } from '@planmax/shared-types'

type ViewPresetRow = Database['public']['Tables']['location_view_presets']['Row']
type ViewPresetInsert = Database['public']['Tables']['location_view_presets']['Insert']

export type { ViewPresetRow }

/**
 * Fetch all view presets for a location (non-deleted, ordered by sort_order)
 */
export async function fetchViewPresets(locationId: string): Promise<ViewPresetRow[]> {
  const { data, error } = await supabase
    .from('location_view_presets')
    .select('*')
    .eq('location_id', locationId)
    .is('deleted_at', null)
    .order('sort_order')
    .order('created_at')

  if (error) throw error
  return data || []
}

/**
 * Create a new view preset
 */
export async function createViewPreset(
  data: Pick<ViewPresetInsert, 'organization_id' | 'location_id' | 'name' | 'center_lng' | 'center_lat' | 'zoom' | 'pitch' | 'bearing'>
): Promise<ViewPresetRow> {
  const { data: row, error } = await supabase
    .from('location_view_presets')
    .insert(data)
    .select()
    .single()

  if (error) throw error
  return row
}

/**
 * Update a view preset (partial)
 */
export async function updateViewPreset(
  id: string,
  data: Partial<Pick<ViewPresetRow, 'name' | 'center_lng' | 'center_lat' | 'zoom' | 'pitch' | 'bearing' | 'sort_order'>>
): Promise<ViewPresetRow> {
  const { data: row, error } = await supabase
    .from('location_view_presets')
    .update(data)
    .eq('id', id)
    .select()
    .single()

  if (error) throw error
  return row
}

/**
 * Soft delete a view preset
 */
export async function deleteViewPreset(id: string): Promise<void> {
  const { error } = await supabase
    .from('location_view_presets')
    .update({ deleted_at: new Date().toISOString(), is_default: false })
    .eq('id', id)

  if (error) throw error
}

/**
 * Set a preset as the default for its location.
 * Unsets the current default first, then sets the new one.
 */
export async function setDefaultPreset(presetId: string, locationId: string): Promise<void> {
  // 1. Unset current default(s) for this location
  const { error: unsetError } = await supabase
    .from('location_view_presets')
    .update({ is_default: false })
    .eq('location_id', locationId)
    .eq('is_default', true)
    .is('deleted_at', null)

  if (unsetError) throw unsetError

  // 2. Set the new default
  const { error: setError } = await supabase
    .from('location_view_presets')
    .update({ is_default: true })
    .eq('id', presetId)

  if (setError) throw setError
}

/**
 * Unset the default flag on a preset (make it non-default)
 */
export async function unsetDefaultPreset(presetId: string): Promise<void> {
  const { error } = await supabase
    .from('location_view_presets')
    .update({ is_default: false })
    .eq('id', presetId)

  if (error) throw error
}
